import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Y_MAP_ALL_MODULES, Y_MAP_DEFAULT_OPTIONS, Y_MAP_DEFAULT_STATE } from 'shared/constants/map';
import { useApplicationContext } from 'shared/context/ApplicationContext';
import { useGetMapConfigQuery } from 'shared/store/serverSide/commercial-listing-map/desktop/v2/map/get-config/query';
import { mapPreparedConfigSelector } from 'shared/store/serverSide/commercial-listing-map/desktop/v2/map/get-config/selectors';
import { useGetYandexMapScriptQuery } from 'shared/store/serverSide/yandex-map/2.1/get-yandex-map/query';
import { MapContext } from 'shared/types/MapContext';
import { defineOwnYandexModulesAndLayers } from 'shared/utils/defineOwnYandexModulesAndLayers';

import { useGetMapConstructorOptions } from './useGetMapConstructorOptions';
import { usePropagateErrorToBoundary } from './usePropagateErrorToBoundary';

type CreateMapInstance<T> = (ref: RefObject<T>) => void;

type UseInitYandexMap<T extends HTMLElement> = {
  createMapInstance: CreateMapInstance<T>;
  context: MapContext;
};

export function useInitYandexMap<T extends HTMLElement>(): UseInitYandexMap<T> {
  const context = useApplicationContext();
  const constructorOptions = useGetMapConstructorOptions();
  const propagateError = usePropagateErrorToBoundary();
  const { data: ymapsModule, isSuccess: isLoadMapScriptSuccess } = useGetYandexMapScriptQuery();

  const mapRef = useRef<MapContext['mapRef']['current']>(null);
  const [ymaps, setYMaps] = useState<MapContext['ymaps']>();
  const [map, setMap] = useState<MapContext['map']>();

  /**
   * Как только проинициализировали карты - загружаем их конфигурацию (координаты центра и зум)
   */
  const { data: mapInitialState, isLoading: isMapConfigLoading } = useGetMapConfigQuery({
    enabled: isLoadMapScriptSuccess,
    select: mapPreparedConfigSelector,
  });

  useEffect(() => {
    if (isLoadMapScriptSuccess) {
      defineOwnYandexModulesAndLayers(ymapsModule, Y_MAP_ALL_MODULES)
        .then(initializedYMaps => {
          setYMaps(initializedYMaps);
        })
        .catch(error => {
          context.logger.error(error as Error, {
            domain: 'src/shared/hooks/useInitYandexMap.ts',
            message: '[Yandex Maps] Ошибка при инициализации модулей Яндекс Карт',
          });

          propagateError(error);
        });
    }
  }, [isLoadMapScriptSuccess, ymapsModule, context, propagateError]);

  const createMapInstance = useCallback<CreateMapInstance<T>>(
    ref => {
      if (!ymaps || !ref.current || isMapConfigLoading) {
        return;
      }

      mapRef.current = ref.current;

      const ymap = new ymaps.Map(
        ref.current,
        {
          ...Y_MAP_DEFAULT_STATE,
          ...mapInitialState,
        },
        {
          ...Y_MAP_DEFAULT_OPTIONS,
          ...constructorOptions,
        },
      );

      setMap(ymap);
    },
    [ymaps, isMapConfigLoading, mapInitialState, constructorOptions],
  );

  return useMemo(
    () => ({
      context: {
        map,
        mapRef,
        ymaps,
      },
      createMapInstance,
    }),
    [createMapInstance, map, ymaps],
  );
}
